import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, LabelList,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Users, ShieldCheck, AlertTriangle, XCircle, Globe2, Briefcase, UserCheck, FileWarning, Download, Loader2, Bike, Car } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const VEHICLE_META = {
  "دراجة نارية": { color: "#f97316", icon: Bike },
  "سيارة": { color: "#2563eb", icon: Car },
  "غير محدد": { color: "#cbd5e1", icon: Users },
};

const HEALTH = [
  { key: "valid", tk: "hValid", color: "#10b981" },
  { key: "expiring_soon", tk: "hExpiring", color: "#f59e0b" },
  { key: "expired", tk: "hExpired", color: "#ef4444" },
  { key: "none", tk: "hNone", color: "#cbd5e1" },
];

const Kpi = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={`kpi-${label}`}>
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
    <div>
      <p className="text-2xl font-bold font-mono-nums text-slate-800 leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  </div>
);

const BarList = ({ title, icon: Icon, items, field, onPick, hint, noData }) => {
  const max = items.length ? Math.max(...items.map((i) => i.count)) : 1;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`barlist-${title}`}>
      <p className="font-heading font-bold text-slate-800 mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-blue-600" /> {title}
        <span className="text-xs font-normal text-slate-400">{hint}</span>
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">{noData}</p>
      ) : (
        <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
          {items.map((it) => (
            <button key={it.name} type="button" onClick={() => onPick && onPick(field, it.name)}
              data-testid={`barlist-item-${it.name}`}
              className="w-full text-right group">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 group-hover:text-blue-700 transition">{it.name}</span>
                <span className="font-mono-nums font-bold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 text-xs">{it.count}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 group-hover:bg-blue-600 transition-all"
                  style={{ width: `${Math.max(6, (it.count / max) * 100)}%` }} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AnalyticsPanel = ({ onFilter, refreshKey, branchOptions = [], onMissingDoc }) => {
  const { t, dir } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState("all");
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { api } = await import("../lib/api");
      setData(await api.analytics(branch));
    } finally {
      setLoading(false);
    }
  }, [branch]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportImage = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(captureRef.current, { scale: 2, backgroundColor: "#f8fafc", useCORS: true });
      const link = document.createElement("a");
      link.download = `${t("dash.analytics.imgName")}-${branch === "all" ? t("dash.analytics.allBranchesFile") : branch}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(t("dash.analytics.downloaded"));
    } catch (e) {
      toast.error(t("dash.analytics.exportFail"));
    } finally {
      setExporting(false);
    }
  };

  const healthData = data
    ? HEALTH.map((h) => ({ name: t(`dash.analytics.${h.tk}`), value: data.health[h.key] || 0, color: h.color })).filter((d) => d.value > 0)
    : [];
  const missing = data ? data.missing_documents.filter((d) => d.missing > 0) : [];
  const vehicleData = data
    ? (data.by_vehicle || []).map((v) => ({ name: v.name, value: v.count, color: (VEHICLE_META[v.name] || VEHICLE_META["غير محدد"]).color })).filter((d) => d.value > 0)
    : [];
  const bikeCount = (data?.by_vehicle || []).find((v) => v.name === "دراجة نارية")?.count || 0;
  const carCount = (data?.by_vehicle || []).find((v) => v.name === "سيارة")?.count || 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="analytics-panel" dir={dir}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-heading font-bold text-slate-800">
          {t("dash.analytics.title")} <span className="text-slate-400 font-normal text-sm">— {branch === "all" ? t("dash.analytics.allBranches") : branch}</span>
        </p>
        <div className="flex items-center gap-2">
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="analytics-branch-select" className="w-40">
              <SelectValue placeholder={t("dash.analytics.allBranches")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.analytics.allBranches")}</SelectItem>
              {branchOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" data-testid="analytics-export-button" disabled={exporting || loading}
            className="gap-1.5" onClick={exportImage}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">{t("dash.analytics.exportImg")}</span>
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-16 text-center text-slate-400">{t("dash.analytics.loading")}</div>
      ) : (
       <div ref={captureRef} className="space-y-5 bg-slate-50 p-1 rounded-2xl">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} label={t("dash.analytics.kpiTotal")} value={data.total} color="bg-slate-100 text-slate-700" />
        <Kpi icon={ShieldCheck} label={t("dash.analytics.kpiValid")} value={data.health.valid} color="bg-blue-100 text-blue-700" />
        <Kpi icon={AlertTriangle} label={t("dash.analytics.kpiExpiring")} value={data.health.expiring_soon} color="bg-amber-100 text-amber-700" />
        <Kpi icon={XCircle} label={t("dash.analytics.kpiExpired")} value={data.health.expired} color="bg-red-100 text-red-700" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="vehicle-donut">
        <p className="font-heading font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Bike className="h-4 w-4 text-orange-500" /> {t("dash.analytics.vehicleDist")}
        </p>
        {vehicleData.length === 0 ? <p className="text-sm text-slate-400">{t("dash.analytics.noVehicleData")}</p> : (
          <>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <ResponsiveContainer width="45%" height={200} minWidth={180}>
              <PieChart>
                <Pie data={vehicleData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}
                  onClick={(d) => onFilter && d?.name && onFilter("vehicle", d.name)} className="cursor-pointer">
                  {vehicleData.map((d, i) => <Cell key={i} fill={d.color} cursor="pointer" />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} ${t("dash.analytics.employeeUnit")}`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-1 gap-3 flex-1 w-full">
              <button type="button" onClick={() => onFilter && onFilter("vehicle", "دراجة نارية")} data-testid="vehicle-bike-count"
                className="flex items-center justify-between rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-right transition hover:bg-orange-100 hover:border-orange-200">
                <span className="flex items-center gap-2 font-semibold text-orange-700"><Bike className="h-5 w-5" /> {t("dash.analytics.bikeRiders")}</span>
                <span className="font-heading text-2xl font-extrabold font-mono-nums text-orange-700">{bikeCount}</span>
              </button>
              <button type="button" onClick={() => onFilter && onFilter("vehicle", "سيارة")} data-testid="vehicle-car-count"
                className="flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-right transition hover:bg-blue-100 hover:border-blue-200">
                <span className="flex items-center gap-2 font-semibold text-blue-700"><Car className="h-5 w-5" /> {t("dash.analytics.carRiders")}</span>
                <span className="font-heading text-2xl font-extrabold font-mono-nums text-blue-700">{carCount}</span>
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-400">{t("dash.analytics.clickHint")}</p>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="health-donut">
          <p className="font-heading font-bold text-slate-800 mb-2">{t("dash.analytics.docHealthDist")}</p>
          {healthData.length === 0 ? <p className="text-sm text-slate-400">{t("dash.analytics.noData")}</p> : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {healthData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {healthData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600">
                      <span className="h-3 w-3 rounded-full" style={{ background: d.color }} /> {d.name}
                    </span>
                    <span className="font-mono-nums font-semibold text-slate-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="missing-docs-chart">
          <p className="font-heading font-bold text-slate-800 mb-2 flex items-center gap-2">
            <FileWarning className="h-4 w-4 text-amber-500" /> {t("dash.analytics.missingDocs")}
            <span className="text-xs font-normal text-slate-400">{t("dash.analytics.clickColHint")}</span>
          </p>
          {missing.length === 0 ? <p className="text-sm text-blue-600">{t("dash.analytics.allDocsAdded")}</p> : (
            <div dir="ltr">
              <ResponsiveContainer width="100%" height={Math.max(200, missing.length * 30)}>
                <BarChart data={missing} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis type="category" dataKey="label" width={95} tick={{ fontSize: 12, fill: "#475569" }} />
                  <Tooltip formatter={(v) => [`${v} ${t("dash.analytics.employeeUnit")}`, t("dash.analytics.missingLabel")]} cursor={{ fill: "rgba(245,158,11,0.08)" }} />
                  <Bar dataKey="missing" fill="#f59e0b" radius={[0, 6, 6, 0]} cursor="pointer"
                    onClick={(d) => onMissingDoc && onMissingDoc(d.key, d.label)}>
                    <LabelList dataKey="missing" position="right" style={{ fontSize: 12, fill: "#92400e", fontWeight: 700 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BarList title={t("dash.analytics.nationalities")} icon={Globe2} items={data.by_nationality} field="nationality" onPick={onFilter} hint={t("dash.analytics.pickHint")} noData={t("dash.analytics.noData")} />
        <BarList title={t("dash.analytics.professions")} icon={Briefcase} items={data.by_profession} field="profession" onPick={onFilter} hint={t("dash.analytics.pickHint")} noData={t("dash.analytics.noData")} />
        <BarList title={t("dash.analytics.empStatus")} icon={UserCheck} items={data.by_status} field="status" onPick={onFilter} hint={t("dash.analytics.pickHint")} noData={t("dash.analytics.noData")} />
      </div>
      </div>
      )}
    </motion.div>
  );
};
